import React, { useState, useEffect } from "react";
import { payloadToObject } from "./NotificationMonitoring";

function NotificationPayloadInspector({ item }) {

    const [payloadView, setPayloadView] = useState("json");
    const [expandedSections, setExpandedSections] = useState({});

    useEffect(() => {
        setExpandedSections({});
        setPayloadView("json");
    }, [item]);

    if (!item) {
        return null;
    }

    const payloadObj = payloadToObject(item.payload);

    /* =====================================================
       Cascading Payload Logic
       Same logic as NotificationDetailsDrawer
       ===================================================== */

    const topLevelSections = {
        ANY_OFFER_CHANGED: [
            /^Offers\s+\d+$/i,
            /^Summary$/i,
            /^Offer Change Trigger$/i
        ],

        B2B_ANY_OFFER_CHANGED: [
            /^Offers\s+\d+$/i,
            /^Summary$/i,
            /^Offer Change Trigger$/i
        ],

        FBA_INVENTORY_AVAILABILITY_CHANGES: [
            /^Fulfillment Inventory By Marketplace\s+\d+$/i
        ],

        ORDER_CHANGE: [
            /^Summary$/i,
            /^Order Change Trigger$/i
        ],

        PRICING_HEALTH: [
            /^Summary$/i,
            /^Merchant Offer$/i,
            /^Offer Change Trigger$/i
        ],

        ACCOUNT_STATUS_CHANGED: [
            /^Account Status Change Notification$/i
        ]
    };

    const isTopLevelSection = (label) => {

        const patterns =
            topLevelSections[item.type] || [];

        return patterns.some((pattern) =>
            pattern.test(String(label))
        );
    };

    const toggleSection = (key) => {

        setExpandedSections((prev) => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const isExpanded = (key) => {
        return expandedSections[key] === true;
    };

    /* =====================================================
       Arrow
       ===================================================== */

    const Arrow = ({ open }) => (
        <span
            className={`anm-payload-arrow ${
                open
                    ? "anm-arrow-open"
                    : "anm-arrow-closed"
            }`}
        />
    );

    /* =====================================================
       Section Header
       ===================================================== */

    const SectionHeader = ({
        label,
        sectionKey
    }) => {

        const open =
            isExpanded(sectionKey);

        return (
            <div
                className="anm-payload-section-header"
                onClick={() =>
                    toggleSection(sectionKey)
                }
            >

                <span>
                    {label}
                </span>

                <Arrow open={open} />

            </div>
        );
    };

    /* =====================================================
       Child Header
       ===================================================== */

    const ChildHeader = ({ label }) => (
        <div className="anm-payload-child-header">
            {label}
        </div>
    );

    /* =====================================================
       Payload Value
       ===================================================== */

    const ValueRow = ({ item }) => {

        if (
            item.isHeader &&
            item.label
        ) {

            return (
                <ChildHeader
                    label={item.label}
                />
            );
        }

        if (
            !item.label ||
            item.label === ":"
        ) {

            return (
                <div className="anm-payload-value-row">

                    {item.value != null &&
                    item.value !== ""
                        ? String(item.value)
                        : "-"}

                </div>
            );
        }

        return (
            <div className="anm-payload-value-row">

                <span className="anm-payload-field-label">
                    {item.label}
                </span>

                <span className="anm-payload-field-separator">
                    :
                </span>

                <span>
                    {item.value != null &&
                    item.value !== ""
                        ? String(item.value)
                        : "-"}
                </span>

            </div>
        );
    };

    /* =====================================================
       Create Sections
       ===================================================== */

    const createSections = () => {

        const sections = [];
        let currentSection = null;

        (
            Array.isArray(item.payload)
                ? item.payload
                : []
        ).forEach((payloadItem) => {

            if (
                payloadItem.isHeader &&
                isTopLevelSection(
                    payloadItem.label
                )
            ) {

                currentSection = {
                    label: payloadItem.label,
                    items: []
                };

                sections.push(
                    currentSection
                );

            } else if (currentSection) {

                currentSection.items.push(
                    payloadItem
                );

            } else {

                sections.push({
                    label: null,
                    items: [payloadItem]
                });

                currentSection =
                    sections[
                        sections.length - 1
                    ];
            }
        });

        return sections;
    };

    /* =====================================================
       Render Section
       ===================================================== */

    const renderSection = (
        section,
        index
    ) => {

        /*
         * Normal fields before a section
         */
        if (!section.label) {

            return section.items.map(
                (payloadItem, itemIndex) => (
                    <ValueRow
                        key={itemIndex}
                        item={payloadItem}
                    />
                )
            );
        }

        const sectionKey =
            `section-${index}`;

        const open =
            isExpanded(sectionKey);

        return (
            <div
                key={sectionKey}
                className="anm-payload-section"
            >

                <SectionHeader
                    label={section.label}
                    sectionKey={sectionKey}
                />

                {open && (

                    <div className="anm-payload-section-content">

                        {section.items.map(
                            (payloadItem, itemIndex) => (
                                <ValueRow
                                    key={itemIndex}
                                    item={payloadItem}
                                />
                            )
                        )}

                    </div>

                )}

            </div>
        );
    };

    const sections = createSections();

    return (
        <div>

            {/* =================================================
                Payload Header
            ================================================= */}

            <div className="anm-payload-header">

                <div className="anm-detail-label mb-0">
                    Payload Data
                </div>

                <div className="anm-view-toggle">

                    <button
                        type="button"
                        className={
                            payloadView === "json"
                                ? "active"
                                : ""
                        }
                        onClick={() =>
                            setPayloadView("json")
                        }
                    >
                        JSON
                    </button>

                    <button
                        type="button"
                        className={
                            payloadView === "data"
                                ? "active"
                                : ""
                        }
                        onClick={() =>
                            setPayloadView("data")
                        }
                    >
                        Data View
                    </button>

                </div>

            </div>


            {/* =================================================
                JSON
            ================================================= */}

            {payloadView === "json" && (

                <pre className="anm-payload-pre">
                    {JSON.stringify(
                        payloadObj,
                        null,
                        2
                    )}
                </pre>

            )}


            {/* =================================================
                DATA VIEW
            ================================================= */}

            {payloadView === "data" && (

                <div className="anm-payload-cascade">

                    {sections.length === 0 ? (

                        <div className="anm-no-payload">
                            No payload fields
                        </div>

                    ) : (

                        sections.map(
                            (section, index) =>
                                renderSection(
                                    section,
                                    index
                                )
                        )

                    )}

                </div>

            )}

        </div>
    );
}

export default NotificationPayloadInspector;